// AUTH-02c (#1520 Part 1b, #2172) — shared cold-start-safe consumer for the
// native Google/Apple OAuth callback.
//
// Android now delivers the OAuth success code on the verified HTTPS App Link
// (https://app.usetenon.com/auth/callback) instead of the hijackable custom
// scheme (com.usetenon.app://auth), so any code that can see an `appUrlOpen`
// URL or a cold-start launch URL must recognize BOTH shapes and run the same
// PKCE exchange. Two call sites need this: the always-mounted app-root
// listener in App.jsx (so a cold-started callback isn't silently dropped when
// Android evicts the app while the Custom Tab is foreground) and LoginPage's
// listener (mounted only while /login is on screen). Sharing this module is
// what keeps the exchange logic — and the single-use code dedup guard — from
// diverging between them.
//
// UI side effects (navigation, toasts, closing the in-app browser) are
// deliberately NOT here — callers map the returned result to whatever's right
// for their context (a hard redirect in App.jsx vs react-router + toast in
// LoginPage).

import { auth, setToken, setRefreshToken } from './api'
import { takeCodeVerifier } from './oauth-pkce'

export type OAuthCallbackResult =
  | { kind: 'ignored' }
  | { kind: 'already-exchanged' }
  | { kind: 'auth-success'; next: string }
  | { kind: 'auth-error'; errCode: string }
  | { kind: 'no-account' }

// Module scope (not component/instance state): must survive remount AND be
// shared across every listener that can observe this URL, or a doubled
// foreground + cold-start delivery of the same callback spends the
// single-use code twice — the loser would toast a spurious failure over a
// successful login.
const exchangedOAuthCodes = new Set<string>()

export function isOAuthCallbackURL(url: string): boolean {
  return url.startsWith('com.usetenon.app://') ||
    url.startsWith('https://app.usetenon.com/auth/callback')
}

/**
 * Exchange a single-use OAuth code for a token pair, guarded by the shared
 * dedup Set. Exported on its own for the iOS ASWebAuthenticationSession path
 * (LoginPage.handleGoogleLogin), which gets its code back as a direct return
 * value rather than through an `appUrlOpen` URL — but must still share the
 * dedup guard with consumeOAuthCallbackURL(), since on iPad the appUrlOpen
 * listener can also fire for a callback ASWebAuthenticationSession already
 * returned, and the loser would otherwise spend a dead code.
 */
export async function exchangeOAuthCode(code: string, next: string): Promise<OAuthCallbackResult> {
  if (exchangedOAuthCodes.has(code)) return { kind: 'already-exchanged' }
  exchangedOAuthCodes.add(code)
  const verifier = await takeCodeVerifier()
  if (!verifier) return { kind: 'auth-error', errCode: 'verifier_missing' }
  try {
    const res = await auth.nativeExchange(code, verifier)
    if (res.refresh_token) await setRefreshToken(res.refresh_token)
    await setToken(res.token)
    return { kind: 'auth-success', next: res.next || next }
  } catch {
    return { kind: 'auth-error', errCode: 'exchange_failed' }
  }
}

export async function consumeOAuthCallbackURL(url: string): Promise<OAuthCallbackResult> {
  if (!isOAuthCallbackURL(url)) return { kind: 'ignored' }

  const isAppLink = url.startsWith('https://app.usetenon.com/auth/callback')
  // H2 belt-and-suspenders: some WKWebView builds treat custom-scheme URLs as
  // opaque URIs and return an empty parsed.host. Regex stays the primary
  // dispatcher; new URL() below is only used for searchParams. See issue #79
  // (SIWA -> /login loop, builds 25-36).
  const isAuth      = isAppLink || /^com\.usetenon\.app:\/\/auth(\?|$)/.test(url)
  const isSignup    = /^com\.usetenon\.app:\/\/signup(\?|$)/.test(url)
  const isNoAccount = /^com\.usetenon\.app:\/\/no-account(\?|$)/.test(url)
  if (!isAuth && !isSignup && !isNoAccount) return { kind: 'ignored' }

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return { kind: 'ignored' }
  }

  // `no-account` is the native no-Tenon-account SSO redirect; the legacy
  // `signup` deep-link is treated identically — the native app never shows a
  // signup form (App Store Guideline 4 + 3.1.1: no mobile account creation).
  if (isNoAccount || isSignup) return { kind: 'no-account' }

  const next = parsed.searchParams.get('next') || '/projects'

  // AUTH-02 (#728): the callback carries a single-use code, not tokens. Trade
  // it for a token pair over an authenticated POST that proves possession of
  // the PKCE verifier stashed before the browser opened. An app that raced
  // this intent holds a code it can't spend.
  const code = parsed.searchParams.get('code')
  if (code) return exchangeOAuthCode(code, next)

  // AUTH-02c Part 2 (#1520): the LEGACY token-in-URL path is RETIRED. The
  // server no longer emits raw `token`/`refresh_token` to the custom scheme —
  // a native build predating the PKCE code flow (#728) now receives
  // `?error=upgrade_required` instead (handled below). A stray `?token=…`
  // callback therefore falls through to `ignored`; it is never trusted as a
  // session, closing the last hijackable-scheme raw-token path.

  // Error redirect from appleErrorRedirect / googleErrorRedirect, e.g.
  // com.usetenon.app://auth?error=oauth_cancelled
  const errCode = parsed.searchParams.get('error')
  if (errCode) return { kind: 'auth-error', errCode }

  return { kind: 'ignored' }
}
